import { EventEmitter, h, Component, Host, Prop, State, Watch, Element, Event } from '@stencil/core';

import type { TableColumnInterface, TableStateInterface } from '../../../../services/interfaces';
import { TableOrderInterface } from '../../../../services/interfaces';
import { CONTENT_ALIGNMENT } from '../helpers/constants';
import { getTableState, setTableColumnOptions } from '../helpers/table-state';

@Component({
  tag: 'pk-table-header-col',
  styleUrl: './scss/pk-table-header-col.scss',
  scoped: true,
})
export class PkTableHeaderCol {
  // Own Properties
  // ====================

  private tableState: TableStateInterface;

  // Reference to host HTML element
  // ===================================

  @Element() el: HTMLPkTableHeaderColElement;

  // State() variables
  // Inlined decorator, alphabetical order
  // =======================================

  @State() _isHover: boolean;

  // Public Property API
  // ========================

  /** Column key identifier */
  @Prop({ reflect: true }) columnKey: string | number;

  /** If true, the width of the columns are set to be usePercentageWidth (%), otherwise will be set as fixed width (px) */
  @Prop({ reflect: true }) usePercentageWidth: boolean;

  /** If true, the entire column will be hidden with display none*/
  @Prop({ reflect: true }) isHidden: boolean;

  /** Changes the cell content alignment. */
  @Prop({ reflect: true }) aligned?: string = 'left';

  /** If true, pkTableSortBy event will be fired allowing developers to execute their sorting function
   *  depending on the columnKey and order in matter */
  @Prop({ reflect: true }) sortable: boolean;

  /** Default sorting value if the the column is not the sorted one */
  @Prop({ reflect: true }) defaultSortOrder: TableOrderInterface = 'DESC';

  /** The width of the column */
  @Prop({ reflect: true }) width: string | number;

  /** Specify if column width can shrink. */
  @Prop({ reflect: true }) canShrink: boolean;

  /** Specify if column width can grow. */
  @Prop({ reflect: true }) canGrow: boolean;

  // Prop lifecycle events
  // =======================

  @Watch('width')
  setWidthSize() {
    this.setColumnStateAttribute();
  }

  @Watch('isHidden')
  @Watch('canShrink')
  @Watch('canGrow')
  setHiddenVisibility() {
    this.setColumnStateAttribute();
  }

  // Events section
  // Requires JSDocs for public API documentation
  // ==============================================

  /** Handler to be called when the header column cell is clicked */
  @Event() pkTableHeaderColClick: EventEmitter<{
    columnKey: TableColumnInterface['columnKey'];
    order: TableOrderInterface;
  }>;

  // Component lifecycle events
  // Ordered by their natural call order
  // =====================================

  componentWillLoad() {
    const tableId = this.el.closest<HTMLPkTableElement>('pk-table[table-id]')?.tableId;
    this.tableState = getTableState(tableId);
    if (!this.tableState) return;

    this.setColumnStateAttribute();
  }

  componentDidLoad() {
    this.setWidthSize();
    this.attachEventListeners();
  }

  disconnectedCallback() {
    this.clearEventListeners();
  }

  // Listeners
  // ==============

  // Public methods API
  // These methods are exposed on the host element.
  // Always use two lines.
  // Public Methods must be async.
  // Requires JSDocs for public API documentation.
  // ===============================================

  // Local methods
  // Internal business logic.
  // ==========================

  private attachEventListeners = () => {
    this.el.addEventListener('click', this.sortAscHandler);
    this.el.addEventListener('mouseenter', this.toggleIsHover);
    this.el.addEventListener('mouseleave', this.toggleIsHover);
  };

  private clearEventListeners = () => {
    this.el.removeEventListener('click', this.sortAscHandler);
    this.el.removeEventListener('mouseenter', this.toggleIsHover);
    this.el.removeEventListener('mouseleave', this.toggleIsHover);
  };

  private setColumnStateAttribute = () => {
    setTableColumnOptions(this.tableState, {
      columnKey: this.columnKey,
      isHidden: this.isHidden,
      usePercentageWidth: this.usePercentageWidth,
      width: this.width,
      canShrink: this.canShrink,
      canGrow: this.canGrow,
    });
  };

  private sortAscHandler = () => {
    if (!this.sortable) return;

    if (!this.tableState) return;

    const { sortBy } = this.tableState;

    let order = this.defaultSortOrder;
    if (sortBy?.columnKey === this.columnKey) {
      order = sortBy?.order === 'DESC' ? 'ASC' : 'DESC';
    }
    this.tableState.sortBy.columnKey = this.columnKey;
    this.tableState.sortBy.order = order;
    this.emitClickEvent(order);
  };

  private toggleIsHover = () => {
    if (!this.sortable) {
      return;
    }

    this._isHover = !this._isHover;
  };

  private emitClickEvent = (order: TableOrderInterface) => {
    this.pkTableHeaderColClick.emit({ columnKey: this.columnKey, order });
  };

  private get cellAlignment() {
    if (this.aligned && !CONTENT_ALIGNMENT.includes(this.aligned)) {
      console.warn(`[pk-table-cell]: 'aligned' property should be one of ${String(CONTENT_ALIGNMENT)}`);
      return {};
    }
    if (this.aligned === 'center') {
      return {
        '--pk-cell--justify-content': 'center',
        '--pk-cell--text-align': 'center',
      };
    }
    if (this.aligned === 'right') {
      return {
        '--pk-cell--justify-content': 'flex-end',
        '--pk-cell--text-align': 'right',
      };
    }
    return {};
  }

  private get cellVerticalPad() {
    if (!this.tableState) return {};

    const { cellVerticalPad } = this.tableState;

    if (cellVerticalPad < 4 || cellVerticalPad % 4 !== 0) {
      console.warn('Cell padding minimum is 4px, and must be a multiple of 4.');
      return {};
    }
    if (!cellVerticalPad) {
      return {};
    }

    return {
      '--pk-cell--vertical-pad': `${cellVerticalPad}px`,
    };
  }

  private get cellHorizontalPad() {
    if (!this.tableState) return {};

    const { cellHorizontalPad } = this.tableState;

    if (cellHorizontalPad < 8 || cellHorizontalPad % 4 !== 0) {
      console.warn('Cell padding minimum is 8px, and must be a multiple of 4.');
      return {};
    }
    if (!cellHorizontalPad) {
      return {};
    }

    return {
      '--pk-cell--horizontal-pad': `${cellHorizontalPad}px`,
    };
  }

  // render() function
  // Always the last one in the class.
  // ===================================

  render() {
    if (!this.tableState) return null;

    const { sortBy } = this.tableState;

    const style = {
      ...(this.width && { '--pk-cell--width': this.usePercentageWidth ? `${this.width}%` : `${this.width}px` }),
      ...this.cellAlignment,
      ...this.cellVerticalPad,
      ...this.cellHorizontalPad,
    };

    return (
      <Host
        style={style}
        aria-hidden={this.isHidden}
        class={{
          'pk-table--header-col': true,
          'is-sortable': this.sortable,
          'is-active': sortBy?.columnKey === this.columnKey,
          'with-width': !!this.width,
          'is-hidden': this.isHidden,
          'can-grow': !!this.canGrow,
          'can-shrink': !!this.canShrink,
        }}
        role="gridcell"
      >
        <div class={{ 'pk-table--header-label': true, 'is-active': sortBy?.columnKey === this.columnKey }}>
          <slot />
        </div>
        {this.sortable && sortBy?.columnKey === this.columnKey && (
          <pk-icon
            name={sortBy?.order === 'DESC' ? 'navigation-chevron-down' : 'navigation-chevron-up'}
            color="interaction"
          />
        )}
        {this.sortable && this._isHover && sortBy?.columnKey !== this.columnKey && (
          <pk-icon name={this.defaultSortOrder === 'DESC' ? 'navigation-chevron-down' : 'navigation-chevron-up'} />
        )}
        {this.sortable && !this._isHover && sortBy?.columnKey !== this.columnKey && (
          <pk-icon
            name={this.defaultSortOrder === 'DESC' ? 'navigation-chevron-down' : 'navigation-chevron-up'}
            color="text-02"
          />
        )}
      </Host>
    );
  }
}
